import { computed, defineComponent, PropType, toRaw } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapState } from "pinia";
import moment from "moment";
import GoogleMaps = google.maps;

import config from "../../../../../common/config";
import { useApp } from "../../../../App/useApp";
import { BranchesService } from "../../../../../services/branches-service";
import { ToastrService } from "../../../../../services/toastr.service";
import { CalendarMapService } from "../../../../../services-vue/calendar-map-service";
import { PermissionManager } from "../../../../../common/utilities/permission-manager";
import { AnalyticsCategory, AnalyticsService } from "../../../../../services/analytics.service";
import {
    INearbyClient,
    INearbyClientsPayload,
    INearbyClientsResItem,
    INearbyClientsTask,
    PatientService
} from "../../../../../services/patient.service";
import { TaskService } from "../../../../../services/task.service";
import { adjustDate } from "../../../../../common/adjust-date";
import {
    ApplicationEnum,
    EvvStatusHomeCareEnum,
    EvvStatusHomeHealthEnum,
    ParentPermissionEnum,
    PatientStatusEnum,
    PermissionActionEnum
} from "../../../../../enums/enums";
import { getSVGIcon } from "../../../VisitMapSVGs";
import { debounce } from "../../../../../common/vue-helpers/debounce";
import {
    getApplicationId,
    getProductFullName,
    getProductPillClass
} from "../../../../../common/vue-helpers/modifiers/application-modifiers";
import {
    formatFullAddress,
    formatPhone,
    formatScheduledTime,
    formatTimeInOut
} from "../../../../../common/vue-helpers/modifiers/value-modifier";
import { nonEvvVisitStatus } from "../DailyCalendarContent/DailyCalendarContent";
import { IVisitMapDetails, IVisitMapRequestParams } from "../../../../../resources-vue/vue-interfaces/i-visit-map";
import { IAddCalendarMarkerOptions } from "../../../../../resources-vue/vue-interfaces/i-calendar-map";
import { ISelectedEmployee } from "../../../../../resources-vue/vue-interfaces/i-reassign-visit";

import ProductPill from "../../../../../resources-vue/vue-elements/ProductPill/ProductPill.vue";
import ScheduleVisitDialog from "../../../../../resources-vue/vue-dialogs/ScheduleVisit/ScheduleVisit.vue";

export default defineComponent({
    components: {
        ProductPill,
        ScheduleVisitDialog
    },

    props: {
        selectedEmployeeDetails: {
            default: {} as any
        },
        toggleFindNearByClientsView: { type: Function as PropType<(details?: any) => void>, required: true },
        date: { type: String, default: moment().format("MM/DD/YYYY") },
        weekDayRange: { type: Number, default: 1 }
    },

    emits: ["update:showNearbyClients", "calendarRefresh"],

    data() {
        return {
            userOs: "",
            _branchService: null as BranchesService,
            _patientService: null as PatientService,
            _toastrService: null as ToastrService,
            _calendarMapService: null as CalendarMapService,
            _permissionManager: null as PermissionManager,
            _analyticsService: null as AnalyticsService,
            _taskService: null as TaskService,
            _employeeMarker: null as GoogleMaps.Marker,
            _bounds: null as GoogleMaps.LatLngBounds,
            _map: null as GoogleMaps.Map,
            observer: null as IntersectionObserver,
            targetElement: null as HTMLDivElement,
            branchList: [] as string[],
            agencyList: [] as string[],
            organizationName: "",
            isHomeCareAddAllowed: false as boolean,
            isHosAddAllowed: false as boolean,
            isPalAddAllowed: false as boolean,
            isHomeHealthAddAllowed: false as boolean,
            showLoading: true as boolean,
            nearbyClientSearchTermDebounce: null as ((...args: any) => void) | null,
            nearbyClientSearchTerm: null as string,
            nearbyClientDistance: 15 as number, // default to 15 miles
            pageCount: 0 as number,
            totalPageCount: 1 as number,
            totalItemCount: 0 as number,
            startDate: null as Date,
            endDate: null as Date,
            selectedNearbyClient: {} as INearbyClient,
            nearbyClientPinsList: [] as INearbyClient[],
            nearbyClientPanelList: [] as INearbyClientsResItem[],
            dialogRef: null,
            formatPhone,
            getApplicationId,
            patientStatuses: Object.create(PatientStatusEnum),
            canLoadMore: false as boolean
        };
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    created() {
        this.userOs = window.sessionStorage.getItem("userOs");
        this._branchService = Container.instance.get(BranchesService);
        this._patientService = Container.instance.get(PatientService);
        this._toastrService = Container.instance.get(ToastrService);
        this._calendarMapService = Container.instance.get(CalendarMapService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._analyticsService = Container.instance.get(AnalyticsService);
        this._taskService = Container.instance.get(TaskService);
    },

    computed: {
        ...mapState(useApp, ["organizations"])
    },

    async mounted() {
        this.organizationName = window.localStorage.getItem(config.organizationName);
        this.agencyList = this.organizations
            .find((organization: { name: string }) => organization.name === this.organizationName)
            .agencies?.map((agency) => agency.id);
        const branches = await this._branchService.getAllBranches();
        this.branchList = branches
            .filter(
                (branch) =>
                    branch.application == this.selectedEmployeeDetails?.Application &&
                    branch.accountId == this.selectedEmployeeDetails?.AgencyId
            )
            ?.map((branch) => branch.id);
        this.checkPermissions();
        this.getWeekRangeDates();
        await this._calendarMapService.loadMapsApi();
        await this.setUpEmployeeDetails();
        this.targetElement = this.$refs.trigger as HTMLDivElement;
        this.nearbyClientSearchTermDebounce = debounce(() => this.loadMoreClientList(true), 1200);

        this.observer = new IntersectionObserver(
            (entries: any) => {
                if (entries[0].isIntersecting && this.canLoadMore) this.loadMoreClientList();
            },
            { root: null, threshold: 0 }
        );
        this.observer.observe(this.targetElement);
        this.loadMoreClientList(true);
    },

    watch: {
        nearbyClientSearchTerm: async function () {
            this.nearbyClientSearchTermDebounce();
        },
        nearbyClientDistance: async function () {
            await this.loadMoreClientList(true);
        }
    },

    methods: {
        checkPermissions() {
            this.isHomeCareAddAllowed = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.scheduler,
                PermissionActionEnum.canAdd,
                ApplicationEnum.HomeCare
            );
            this.isHosAddAllowed = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.scheduler,
                PermissionActionEnum.canAdd,
                ApplicationEnum.AxxessHospiceFE
            );
            this.isPalAddAllowed = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.scheduler,
                PermissionActionEnum.canAdd,
                ApplicationEnum.AxxessPalliative
            );
            this.isHomeHealthAddAllowed = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.scheduler,
                PermissionActionEnum.canAdd,
                ApplicationEnum.AgencyCore
            );
        },
        isAddTaskAllowed(application: ApplicationEnum) {
            return (
                ((application == ApplicationEnum.AxxessHospiceFE || application == ApplicationEnum.AxxessHospice) &&
                    this.isHosAddAllowed) ||
                (application == ApplicationEnum.AxxessPalliative && this.isPalAddAllowed) ||
                (application == ApplicationEnum.HomeCare && this.isHomeCareAddAllowed) ||
                (application == ApplicationEnum.AgencyCore && this.isHomeHealthAddAllowed)
            );
        },
        getWeekRangeDates() {
            const currentDate = new Date(this.date);
            let dayRequested = this.weekDayRange - 1;
            if (dayRequested < 0) {
                dayRequested = 0;
            }
            let offset = dayRequested - currentDate.getDay();
            if (offset > 0) {
                offset -= 7;
            }
            this.startDate = adjustDate(currentDate, offset);
            this.endDate = adjustDate(this.startDate, 6);
        },

        async setUpEmployeeDetails() {
            const mapFilterOptions: IVisitMapRequestParams = {
                date: this.date,
                agencies: this.agencyList,
                credentials: this.selectedEmployeeDetails?.Credentials,
                locationIds: this.branchList,
                visitStatus: []
            };

            let mapVisitsList: any = [];
            if (mapFilterOptions.agencies.length != 0 && mapFilterOptions.locationIds.length !== 0) {
                const allVisits = (await this._taskService.getCalendarMapVisits(
                    mapFilterOptions
                )) as unknown as IVisitMapDetails[];
                mapVisitsList = allVisits?.filter((visit) => {
                    if (visit.Application == ApplicationEnum.AxxessPalliative) {
                        return (
                            visit.EmployeeName.FirstName.toLowerCase() ===
                                this.selectedEmployeeDetails?.FirstName.toLowerCase() &&
                            visit.EmployeeName.LastName.toLowerCase() ===
                                this.selectedEmployeeDetails?.LastName.toLowerCase()
                        );
                    } else {
                        return visit.UserId == this.selectedEmployeeDetails?.UserId;
                    }
                });
            }
            this.markEmployeeLocation(mapVisitsList);
        },

        markEmployeeLocation(empMapVisitList: IVisitMapDetails) {
            try {
                const lat: number = this.selectedEmployeeDetails?.Latitude;
                const lon: number = this.selectedEmployeeDetails?.Longitude;
                this._bounds = this._calendarMapService.getBounds();
                const icon = {
                    url:
                        "data:image/svg+xml;charset=UTF-8," +
                        encodeURIComponent(getSVGIcon(this.selectedEmployeeDetails?.Credentials)),
                    scaledSize: new google.maps.Size(50, 50)
                };
                if (this._map === null) {
                    this._map = this._calendarMapService.getMap(document.getElementById("NearbyClient__VisitMap"), {
                        center: {
                            lat: lat,
                            lng: lon
                        },
                        zoom: 18, // Higher is zoom in and lower is zoom out
                        zoomControl: true,
                        mapTypeControl: false,
                        scaleControl: false,
                        streetViewControl: false,
                        rotateControl: false,
                        fullscreenControl: false,
                        gestureHandling: "greedy"
                    });
                }
                this._employeeMarker = this._calendarMapService.addMarker({
                    position: {
                        lat: lat,
                        lng: lon
                    },
                    map: this._map,
                    icon: icon
                });
                let content = this.getEmployeeInfoWindowContent(empMapVisitList, this.selectedEmployeeDetails);

                const info: any = this._calendarMapService.createInfoWindow(content);
                // Marker Events
                this._employeeMarker.addListener("click", () => {
                    info.open(this._map, this._employeeMarker);
                });

                this._bounds.extend(this._employeeMarker.getPosition());
            } catch (error: any) {
                console.error(error?.message);
            }
        },

        getClientInfoWindowContent(visits: INearbyClientsTask[], client: INearbyClient): string {
            this._analyticsService.logEvent({
                category: AnalyticsCategory.CentralCalendarNavigation,
                action: "Central Find Near By Client visit info"
            });
            let patientVisits: string = "";
            const patientName = this.formatName(client?.FirstName, client?.LastName);
            let tempApplicationId: ApplicationEnum;
            if (client.Application === 256) {
                tempApplicationId = ApplicationEnum.AxxessHospice;
            } else if (client.Application === 65536) {
                tempApplicationId = ApplicationEnum.AxxessPalliative;
            } else {
                tempApplicationId = client.Application;
            }

            for (const v of visits) {
                patientVisits += this.getVisitDetails(
                    v,
                    this.selectedEmployeeDetails?.FirstName,
                    this.selectedEmployeeDetails?.LastName,
                    false
                );
            }

            return ` <div id="${client.Id}" class="visit-marker-details">
                        <span class="${getProductPillClass(tempApplicationId)} pill">
                            ${getProductFullName(tempApplicationId)}
                        </span>
                        <big class="info-header"><b>${patientName}</b></big>
                        ${patientVisits}
                        <div class="visit-address">${!!visits[0]?.PatientAddress ? formatFullAddress(visits[0]?.PatientAddress) : ""}</div>
                        </div>`;
        },

        getEmployeeInfoWindowContent(visits: any, emp: any): string {
            this._analyticsService.logEvent({
                category: AnalyticsCategory.CentralCalendarNavigation,
                action: "Central Find Near By Client Employee visit info"
            });
            let inProgressVisits: string = "";
            let completedVisits: string = "";
            let notYetStartedVisits: string = "";
            let status = "";
            for (let v of visits) {
                status = v.Status;
                if (nonEvvVisitStatus.notYetStarted.includes(status.toLowerCase())) {
                    notYetStartedVisits += this.getVisitDetails(v, v.PatientName.FirstName, v.PatientName.LastName);
                } else if (nonEvvVisitStatus.inProgress.includes(status.toLowerCase())) {
                    inProgressVisits += this.getVisitDetails(v, v.PatientName.FirstName, v.PatientName.LastName);
                } else {
                    completedVisits += this.getVisitDetails(v, v.PatientName.FirstName, v.PatientName.LastName);
                }
            }

            let empName = this.formatName(emp?.FirstName, emp?.LastName) || "Unassigned";
            let tempApplicationId: ApplicationEnum;
            if (emp.Application === 256) {
                tempApplicationId = ApplicationEnum.AxxessHospice;
            } else {
                tempApplicationId = emp.Application;
            }

            let employeeCredential = null;
            if (visits?.length) {
                employeeCredential = visits[0]?.EmployeeCredential;
            } else {
                employeeCredential = emp.Agencies?.find((agency) =>
                    // if agency application is BE recoginzed hospice check if user is hospiceFe else just check if they have same application
                    agency.Application === ApplicationEnum.AxxessHospice
                        ? emp.Application === ApplicationEnum.AxxessHospiceFE
                        : emp.Application === agency.Application
                )?.Credentials;
            }

            return ` <div id="${emp.UserId}" class="visit-marker-details">
                            <span class="${getProductPillClass(tempApplicationId)} pill">
                                ${getProductFullName(tempApplicationId)}
                            </span>
                        <big class="info-header"><b>${empName?.trim()}${
                            empName == "Unassigned" ? "" : !!employeeCredential ? `, ${employeeCredential}` : ""
                        }</b></big>

                        ${notYetStartedVisits !== "" ? `<div class="visit-type">${status}</div>${notYetStartedVisits}` : ""}
                        ${inProgressVisits !== "" ? `<div class="visit-type">In Progress</div>${inProgressVisits}` : ""}
                        ${completedVisits !== "" ? `<div class="visit-type">Completed</div>${completedVisits}` : ""}
                        <div class="visit-address">${!!visits[0]?.EmployeeAddress ? formatFullAddress(visits[0]?.EmployeeAddress) : ""}</div>
                        </div>`;
        },

        formatName(firstName: string, lastName: string) {
            return firstName || lastName ? `${firstName || ""} ${lastName || ""}`.trim() : "";
        },

        getVisitDetails(visit: INearbyClientsTask, firstName: string, lastName: string, isEmployee: boolean = true) {
            const visitTime = formatTimeInOut(formatScheduledTime(visit.StartTime), formatScheduledTime(visit.EndTime));

            let evvHouse = null;
            if (visit.Application == ApplicationEnum.HomeCare) {
                if (
                    visit.EvvIconStatus === EvvStatusHomeCareEnum.Finished ||
                    visit.EvvIconStatus === EvvStatusHomeCareEnum.FinishedAdjusted
                ) {
                    evvHouse =
                        visit.EvvIconStatus === EvvStatusHomeCareEnum.FinishedAdjusted
                            ? `<a class='evv'><i class='evv-adjusted signed'></i></a>`
                            : `<a class='evv'><i class='fa fa-home fa-md signed'></i></a>`;
                } else if (
                    visit.EvvIconStatus === EvvStatusHomeCareEnum.InProgress ||
                    visit.EvvIconStatus === EvvStatusHomeCareEnum.InProgressAdjusted
                ) {
                    evvHouse =
                        visit.EvvIconStatus === EvvStatusHomeCareEnum.InProgressAdjusted
                            ? `<a class='evv'><i class='evv-adjusted in-progress'></i></a>`
                            : `<a class='evv'><i class='fa fa-home fa-md'></i></a>`;
                } else if (visit.EvvIconStatus === EvvStatusHomeHealthEnum.NotStarted) {
                    evvHouse = `<a class='evv gray-house'><i class='fa fa-home fa-md not-started'></i></a>`;
                }
            } else if (visit.Application == ApplicationEnum.AgencyCore) {
                if (visit.VerificationVisitStatus === EvvStatusHomeHealthEnum.InProgress) {
                    evvHouse = `<a class='evv ps-1'><i class='fa fa-home fa-md'></i></a>`;
                } else if (
                    visit.VerificationVisitStatus === EvvStatusHomeHealthEnum.PatientVerified ||
                    visit.VerificationVisitStatus === EvvStatusHomeHealthEnum.ClinicianVerified
                ) {
                    evvHouse = `<a class='evv'><i class='fa fa-home fa-md signed'></i></a>`;
                } else if (visit.VerificationVisitStatus === EvvStatusHomeHealthEnum.NotStarted) {
                    evvHouse = `<a class='evv gray-house'><i class='fa fa-home fa-md not-started'></i></a>`;
                }
            }
            return `<div class="visit-details pe-5">
                        <div class="fw-normal">${isEmployee ? visit.VisitName : visit.TaskName}</div>
                        <div>${visit.Status}</div>
                        <div>${visitTime}</div>
                        <div>${isEmployee ? "Scheduled to patient" : "Scheduled with"} ${this.formatName(firstName, lastName)}</div>
                        <div class="evvHouse">${evvHouse ? evvHouse : ""}</div>
                    </div>`;
        },

        async loadMoreClientList(reset: boolean = false) {
            if (reset) {
                this.resetPageData();
            }
            if (this.pageCount < this.totalPageCount) {
                try {
                    const panelListPayload: INearbyClientsPayload = {
                        Term: this.nearbyClientSearchTerm,
                        Distance: Number(this.nearbyClientDistance),
                        AgencyId: this.selectedEmployeeDetails.AgencyId,
                        Application: this.selectedEmployeeDetails.Application,
                        LocationId: this.selectedEmployeeDetails.LocationId,
                        Latitude: this.selectedEmployeeDetails.Latitude,
                        Longitude: this.selectedEmployeeDetails.Longitude,
                        StartDate: moment(this.startDate).format("MM/DD/YYYY"),
                        EndDate: moment(this.endDate).format("MM/DD/YYYY"),
                        CurrentDate: this.date,
                        Page: this.pageCount + 1,
                        PageLength: 50
                    };
                    const response = await this._patientService.getNearByClients(panelListPayload);
                    if (!response) {
                        return this._toastrService.error({
                            title: "Error",
                            message: "There was a problem while getting Nearby Clients. Please try again."
                        });
                    }
                    this.totalPageCount = response.PageCount;
                    this.pageCount++;
                    // Temporary fix: BE sends null if the selected user is not present in the filter criteria
                    const items = response.Items[0] === null ? response.Items.splice(1) : response.Items;
                    const clientsToMark = items.map((panel) => panel.PatientDetails);
                    const markedClientsList = this.markNearbyClientPins(clientsToMark);
                    if (reset) {
                        this.totalItemCount = response.ItemCount;
                        this.nearbyClientPanelList = [...items];
                        this.nearbyClientPinsList = [...markedClientsList];
                    } else {
                        this.totalItemCount = this.totalItemCount + response.ItemCount;
                        this.nearbyClientPanelList = [...this.nearbyClientPanelList, ...items];
                        this.nearbyClientPinsList = [...this.nearbyClientPinsList, ...markedClientsList];
                    }
                    this.showLoading = !(this.nearbyClientPanelList.length === 0);
                    this.canLoadMore = true;
                } catch (error) {
                    console.error(error);
                    this._toastrService.error({
                        title: "Error",
                        message: "There was a problem while getting Nearby Clients. Please try again."
                    });
                }
            } else {
                this.showLoading = false;
            }
        },

        resetPageData() {
            this.pageCount = 0;
            this.totalPageCount = 1;
            this.totalItemCount = 0;
            this.nearbyClientPanelList = [];
            this.canLoadMore = false;
            // remove previous markers
            if (this.nearbyClientPinsList.length) {
                const pins = [...this.nearbyClientPinsList];
                this.clearAllNearbyClientMarkers(pins);
                this.nearbyClientPinsList = [];
            }
        },

        markNearbyClientPins(clientList: INearbyClient[], selectedId?: string) {
            for (let [index, client] of clientList.entries()) {
                const coords = {
                    lat: Number(client.Latitude),
                    lng: Number(client.Longitude)
                };
                let marker = null;
                // used to grey out all the pins except the selected employee pin
                const isCurrentClientSelected = selectedId === client.Id;

                const image = require(`../../../../../assets/images/client-pin.svg`);

                if (!!coords.lat && !!coords.lng) {
                    try {
                        const options: IAddCalendarMarkerOptions = {
                            position: coords,
                            map: this._map,
                            icon: isCurrentClientSelected
                                ? {
                                      url: image,
                                      scaledSize: new google.maps.Size(60, 86)
                                  }
                                : {
                                      url: image
                                  }
                        };
                        marker = this._calendarMapService.addMarker(options);
                        this._analyticsService.logEvent({
                            category: "Create-Marker",
                            action: "Coordinates"
                        });
                        clientList[index].marker = marker;
                        this.initNearbyClientMarkerEvent(clientList[index]);
                    } catch (error) {
                        console.error(error);
                    }
                }
            }
            return clientList;
        },

        initNearbyClientMarkerEvent(client: INearbyClient) {
            if (client.marker) {
                client.marker.addListener("click", async () => {
                    // grey out rest of the pins
                    let clientIdBeforeSelection = this.selectedNearbyClient.Id;
                    this.selectedNearbyClient = client;
                    if (clientIdBeforeSelection !== this.selectedNearbyClient.Id) {
                        this.moveToTop(this.selectedNearbyClient.Id);
                    }
                    this.clearAllNearbyClientMarkers(this.nearbyClientPinsList);
                    this.markNearbyClientPins(this.nearbyClientPinsList, this.selectedNearbyClient?.Id);
                    const patientVisits = this.nearbyClientPanelList?.find(
                        (panel) => panel.PatientDetails.Id == client.Id
                    )?.PatientTask;
                    const content = this.getClientInfoWindowContent(patientVisits, client);
                    const info: any = this._calendarMapService.createInfoWindow(content);
                    info.open(this._map, client.marker);
                });
            }
        },

        clearAllNearbyClientMarkers(locations: INearbyClient[]) {
            for (let [index, client] of locations.entries()) {
                let currentMarker = locations[index].marker;
                this.clearMarker(currentMarker);
            }
        },

        clearMarker(currentMarker: any) {
            if (currentMarker) {
                const rawMarker = toRaw(currentMarker);
                rawMarker.setMap(null);
            }
        },

        async clearNearbyClientFilters() {
            // reset all the filters
            this.nearbyClientSearchTerm = "";
            this.nearbyClientDistance = 15;
            this.pageCount = 0;
            this.totalPageCount = 1;
            this.totalItemCount = 0;
            await this.loadMoreClientList(true);
        },

        async scheduleVisit(clientDetails: INearbyClient) {
            this._analyticsService.logEvent({
                category: AnalyticsCategory.FindNearbyPatient,
                action: "Add task"
            });
            let visitDetails = { ...clientDetails };
            const selectedEmployee: ISelectedEmployee = {
                ...this.selectedEmployeeDetails,
                Id: this.selectedEmployeeDetails?.UserId,
                DisplayName: this.formatDisplayName(this.selectedEmployeeDetails),
                Date: this.date
            };
            this.dialogRef = this.$dialog.open(ScheduleVisitDialog, {
                props: { modal: true, showHeader: false },
                data: {
                    visitDetails,
                    selectedEmployee
                },
                onClose: async (options) => {
                    if (options.data === "success") {
                        this._analyticsService.logEvent({
                            category: AnalyticsCategory.CentralVisitActions,
                            action: "Central Find Near By clients Add task"
                        });
                        this.$emit("calendarRefresh");
                        this.clearMarker(this._employeeMarker);
                        await this.setUpEmployeeDetails();
                    }
                    if (options.data === "reopen") {
                        this.scheduleVisit(clientDetails);
                    }
                }
            });
        },

        formatDisplayName(emp) {
            let displayName = "";
            if (!emp) return displayName;
            const { FirstName, LastName, Credentials, Application } = emp;
            if (FirstName) displayName += FirstName;
            if (LastName) displayName += ` ${LastName} `;
            if (Credentials && Application == ApplicationEnum.HomeCare) displayName += ` ${Credentials}`;
            return displayName.trimEnd();
        },

        highlightClientPin(client: any, clientId: string) {
            this.clearAllNearbyClientMarkers(this.nearbyClientPinsList);
            this.markNearbyClientPins(this.nearbyClientPinsList, clientId);
            this.selectedNearbyClient = client;
        },

        closeNearbyClientPanel() {
            this.toggleFindNearByClientsView();
        },

        getStatusText(status: number): string {
            let result = this.patientStatuses[status].toLowerCase();

            if (result.includes("pending")) {
                return "Pending";
            } else if (result.includes("terminate")) {
                return "Terminated";
            }
            return result;
        },

        getStatusPillClass(status: number): string {
            let result = this.patientStatuses[status].toLowerCase();

            if (result.includes("pending")) {
                return "pending-pill";
            } else if (result.includes("active") || result.includes("hospitalized")) {
                return "active-pill";
            }
            return "discharged-pill";
        },

        moveToTop(id: any) {
            let index = this.nearbyClientPanelList.findIndex((x) => x.PatientDetails.Id === id);

            if (index > 0) {
                let selectedItem = this.nearbyClientPanelList.splice(index, 1)[0];
                this.nearbyClientPanelList.unshift(selectedItem);
            }

            this.$nextTick(() => {
                const scrollContainer = document.querySelector(".nearby-emp-list") as HTMLElement;
                if (scrollContainer) {
                    scrollContainer.scrollTop = 0;
                }
            });
        }
    },

    unmounted() {
        if (this.observer !== null) {
            this.observer.unobserve(this.targetElement);
            this.targetElement = null;
            this.observer = null;
        }
    }
});
